import React from "react";
import ReactECharts from "echarts-for-react";
import { Box, Grid, Stack, styled } from "@mui/material";
import VSNTypography from "../../../shared/VSNTypography";
import useOTBStyle from "../OTBStyle";

const CardStyled = styled(Stack)(({ theme }) => ({
  padding: 15,
  background: "var(--white)",
  boxShadow: "0px 0px 16px 0px #00000014",
  borderRadius: "16px",
  "& .MuiTypography-h4": {
    fontSize: 24,
    fontWeight: 500,
    color: "#202024",
  },
  "& .MuiTypography-h5": {
    fontWeight: 500,
    color: "#898994",
  },
  "& .MuiTypography-h3": {
    fontWeight: 700,
    color: "#000000",
  },
}));

const HPATakeaways = ({ data, pdf }) => {
  const classes = useOTBStyle();

  const barColors = [
    "#B29263",
    "#D5B98B",
    "#E8D0B1",
    "#B3B3B3",
    "#808080",
    "#666666",
    "#444444",
  ];

  const Alcohol = data?.filter((patient) => patient?.hpa_alcohol === 1)?.length;
  const Sleep = data?.filter(
    (patient) => patient?.hpa_sleep_quality === 1
  ).length;
  const Anxiety = data?.filter((patient) => patient?.hpa_anxiety === 1).length;
  const PTSD = data?.filter(
    (patient) => patient?.hpa_traumatic_stress === 1
  ).length;
  const Depression = data?.filter(
    (patient) => patient?.hpa_depression === 1
  ).length;
  const Social_Support = data?.filter(
    (patient) => patient?.hpa_social_support === 1
  ).length;

  const criticalResponses = [
    {
      title: "Alcohol Consumption",
      question:
        "How often do you have a drink containing alcohol? -'4 or more times a week'",
      key: "alcohol_critical_response_1",
    },
    {
      title: "Alcohol Consumption",
      question:
        "How often do you have 6+ drinks on one occasion? -'Daily or almost daily'",
      key: "alcohol_critical_response_2",
    },
    {
      title: "PTSD",
      question:
        "Loss of interest in activities that you used to enjoy. -'Extremely'",
      key: "ptsd_critical_response_1",
    },

    {
      title: "Depression",
      question:
        "Thoughts you would be better off dead or of hurting yourself in some way. -'More than half the days/Nearly every day'",
      key: "depression_critical_response",
    },
    {
      title: "Social Support",
      question:
        "There is a special person who is around when I am in need. -'Very Strongly Disagree/Strongly Disagree",
      key: "social_support_critical_response",
    },
    {
      title: "PTSD",
      question:
        "Taking too many risks or doing things that could cause you harm. -'Extremely'",
      key: "ptsd_critical_response_2",
    },
    {
      title: "Compassion Check",
      question: "I feel connected to others. -'Never' ",
      key: "compassion_check_critical_response",
    },
  ];

  return (
    <>
      {pdf && (
        <Box>
          <Box
            className={classes.pdfReportLayout}
            sx={{ pageBreakAfter: "always" }}
          >
            <VSNTypography variant="h4">
              HPA Takeaways - Critical Responses
            </VSNTypography>
            <Stack gap={2}>
              <Stack direction="row" gap={2}>
                {[
                  {
                    title: "Alcohol Consumption",
                    question:
                      "How often do you have a drink containing alcohol? -'4 or more times a week'",
                    key: "alcohol_critical_response_1",
                  },
                  {
                    title: "Alcohol Consumption",
                    question:
                      "How often do you have 6+ drinks on one occasion? -'Daily or almost daily'",
                    key: "alcohol_critical_response_2",
                  },
                  {
                    title: "PTSD",
                    question:
                      "Loss of interest in activities that you used to enjoy. -'Extremely'",
                    key: "ptsd_critical_response_1",
                  },
                  {
                    title: "PTSD",
                    question:
                      "Taking too many risks or doing things that could cause you harm. -'Extremely'",
                    key: "ptsd_critical_response_2",
                  },
                ]?.map(({ title, key, question }, index) => {
                  const matchingPatients = data?.filter(
                    (patient) => patient[key]?.result === 1
                  ).length;

                  // const questionText =
                  //   data?.find((patient) => patient[key]?.question)?.[key]
                  //     ?.question || "Question not available";
                  return (
                    <Box className={classes.pdfReportCard} key={index}>
                      <VSNTypography variant="h2">{title}</VSNTypography>
                      <VSNTypography component="h5" variant="h5">
                        {question}
                      </VSNTypography>
                      <VSNTypography variant="h2">
                        {matchingPatients ?? 0}
                      </VSNTypography>
                    </Box>
                  );
                })}
              </Stack>
              <Stack direction="row" gap={2}>
                {[
                  {
                    title: "Depression",
                    question:
                      "Thoughts you would be better off dead or of hurting yourself in some way. -'More than half the days/Nearly every day'",
                    key: "depression_critical_response",
                  },
                  {
                    title: "Social Support",
                    question:
                      "There is a special person who is around when I am in need. -'Very Strongly Disagree/Strongly Disagree",
                    key: "social_support_critical_response",
                  },
                  {
                    title: "Compassion Check",
                    question: "I feel connected to others. -'Never' ",
                    key: "compassion_check_critical_response",
                  },
                ]?.map(({ title, key, question }, index) => {
                  const matchingPatients = data?.filter(
                    (patient) => patient[key]?.result === 1
                  ).length;

                  // const questionText =
                  //   data?.find((patient) => patient[key]?.question)?.[key]
                  //     ?.question || "Question not available";
                  return (
                    <Box className={classes.pdfReportCard} key={index}>
                      <VSNTypography variant="h2">{title}</VSNTypography>
                      <VSNTypography component="h5" variant="h5">
                        {question}
                      </VSNTypography>
                      <VSNTypography variant="h2">
                        {matchingPatients ?? 0}
                      </VSNTypography>
                    </Box>
                  );
                })}
              </Stack>

              <ReactECharts
                option={{
                  grid: {
                    bottom: 20,
                    top: 40,
                  },
                  title: {
                    text: "Aggregated Department Risk Summary Based on Unhealthiest Results",
                    top: "0%",
                  },
                  xAxis: {
                    type: "category",
                    data: [
                      "Alcohol",
                      "Sleep",
                      "Anxiety",
                      "PTSD",
                      "Depression",
                      "Social Support",
                    ],
                    name: "Aggregated Department Risk Summary Based on Unhealthiest Results",
                    nameLocation: "middle",
                    nameGap: 100,
                    nameTextStyle: {
                      verticalAlign: "middle",
                    },
                    axisLabel: {
                      formatter: (value) => {
                        return value.split(" ").join("\n");
                      },
                      rotate: 0,
                      interval: 0,
                    },
                  },
                  yAxis: {
                    type: "value",
                    min: 0,
                    max:
                      Math.max(
                        Alcohol,
                        Sleep,
                        Anxiety,
                        PTSD,
                        Depression,
                        Social_Support
                      ) + 10,
                    name: "Count",
                    nameLocation: "middle",
                    nameGap: 40,
                    nameTextStyle: {
                      verticalAlign: "middle",
                    },
                  },
                  series: [
                    {
                      data: [
                        Alcohol,
                        Sleep,
                        Anxiety,
                        PTSD,
                        Depression,
                        Social_Support,
                      ],
                      type: "bar",
                      barWidth: 50,
                      itemStyle: {
                        color: (params) =>
                          barColors[params.dataIndex] || "#000",
                      },
                      animation: false,
                      animationDuration: 0,
                      animationDurationUpdate: 0,
                    },
                  ],
                  tooltip: {
                    trigger: "axis",
                    formatter: (params) => {
                      const data = params[0];
                      return `${data.name}: ${data.value}`;
                    },
                  },
                }}
                style={{ height: "270px", width: "650px" }}
              />
            </Stack>
          </Box>
        </Box>
      )}

      {!pdf && (
        <Box>
          <Box className={classes.reportLayout}>
            <div className={classes.stickyTitle}>
              <VSNTypography variant="h2">
                HPA Takeaways - Critical Responses
              </VSNTypography>{" "}
            </div>
            <Box>
              <Grid container spacing={2}>
                {criticalResponses.map(
                  ({ title, key, question, response }, index) => {
                    const matchingPatients = data?.filter(
                      (patient) => patient[key]?.result === 1
                    ).length;

                    // const questionText =
                    //   data?.find((patient) => patient[key]?.question)?.[key]
                    //     ?.question || "Question not available";

                    return (
                      <Grid item lg={4} sm={6} xs={12} key={index}>
                        <CardStyled>
                          <VSNTypography component="h4" variant="h4">
                            {title}
                          </VSNTypography>
                          <VSNTypography component="h5" variant="h5" py={1}>
                            {question}
                          </VSNTypography>
                          <VSNTypography component="h3" variant="h3">
                            {matchingPatients || 0}
                          </VSNTypography>
                        </CardStyled>
                      </Grid>
                    );
                  }
                )}
              </Grid>
            </Box>
          </Box>

          <Box className={classes.reportLayout}>
            <div className={classes.stickyTitle}>
              <VSNTypography variant="h2">HPA Takeaways</VSNTypography>
            </div>
            <Box sx={{ height: { lg: 550, md: 450, xs: 350 } }}>
              <ReactECharts
                option={{
                  grid: {
                    left: 70,
                    right: 60,
                    bottom: 100,
                  },
                  xAxis: {
                    type: "category",
                    data: [
                      "Alcohol",
                      "Sleep",
                      "Anxiety",
                      "PTSD",
                      "Depression",
                      "Social Support",
                    ],
                    name: "Aggregated Department Risk Summary Based on Unhealthiest Results",
                    nameLocation: "middle",
                    nameGap: 50,
                    nameTextStyle: {
                      verticalAlign: "middle",
                      fontSize: 16,
                      color: "#0A0A0B",
                      fontWeight: 600,
                    },
                    axisLabel: {
                      formatter: (value) => {
                        return value.split(" ").join("\n");
                      },
                      rotate: 0,
                      interval: 0,
                      fontSize: 14,
                      color: "#000",
                      fontWeight: 600,
                    },
                    axisLine: {
                      show: true,
                      lineStyle: {
                        color: "#6E7079",
                        width: 1,
                      },
                    },
                  },
                  autosize: true,
                  yAxis: {
                    type: "value",
                    min: 0,
                    max:
                      Math.max(
                        Alcohol,
                        Sleep,
                        Anxiety,
                        PTSD,
                        Depression,
                        Social_Support
                      ) + 10,
                    name: "Count",
                    nameLocation: "middle",
                    nameGap: 40,
                    nameTextStyle: {
                      fontSize: 14,
                      color: "#0A0A0B",
                      fontWeight: 600,
                    },
                    axisLabel: {
                      fontSize: 14,
                      color: "#000",
                      fontWeight: 600,
                    },
                    axisLine: {
                      show: true,
                      lineStyle: {
                        color: "#6E7079",
                        width: 1,
                      },
                    },
                  },
                  series: [
                    {
                      data: [
                        Alcohol,
                        Sleep,
                        Anxiety,
                        PTSD,
                        Depression,
                        Social_Support,
                      ],
                      type: "bar",
                      barWidth: 50,
                      itemStyle: {
                        color: (params) =>
                          barColors[params.dataIndex] || "#000",
                      },
                      animation: false,
                      animationDuration: 0,
                      animationDurationUpdate: 0,
                    },
                  ],
                  tooltip: {
                    trigger: "axis",
                    formatter: (params) => {
                      const data = params[0] ?? params[1];
                      return `${data.name}: ${data.value}`;
                    },
                  },
                }}
                style={{ height: "100%", width: "100%" }}
              />
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

export default HPATakeaways;
